import { useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, FileText, Loader2, Trash2, UploadCloud } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { validateUpload } from "@/services/onboarding/validation";
import type { UploadedDocument } from "@/types/onboarding";

export function FieldError({ message }: { message?: string | undefined }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-xs font-medium text-destructive">
      {message}
    </p>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: string;
  htmlFor?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  children: React.ReactNode;
  className?: string | undefined;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      <FieldError message={error} />
    </div>
  );
}

/**
 * Searchable single-select. Used for every long, country-dependent list
 * (states, cities, colleges, councils) so no dropdown is ever an endless scroll.
 */
export function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel = "No matches found.",
  loading,
  disabled,
  allowCustom,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  searchPlaceholder?: string | undefined;
  emptyLabel?: string | undefined;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  allowCustom?: boolean | undefined;
  id?: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");

  const filtered = useMemo(() => {
    const query = term.trim().toLowerCase();
    const list = query ? options.filter((option) => option.toLowerCase().includes(query)) : options;
    return list.slice(0, 200);
  }, [options, term]);

  const showCustom =
    Boolean(allowCustom) &&
    term.trim().length > 1 &&
    !options.some((option) => option.toLowerCase() === term.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            "h-11 w-full justify-between font-normal",
            !value && "text-muted-foreground",
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          {loading ? (
            <Loader2 className="size-4 shrink-0 animate-spin opacity-60" />
          ) : (
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder ?? "Search…"}
            value={term}
            onValueChange={setTerm}
          />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {showCustom && (
                <CommandItem
                  value={term}
                  onSelect={() => {
                    onChange(term.trim());
                    setOpen(false);
                    setTerm("");
                  }}
                >
                  Use “{term.trim()}”
                </CommandItem>
              )}
              {filtered.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => {
                    onChange(option);
                    setOpen(false);
                    setTerm("");
                  }}
                >
                  <Check className={cn("size-4", value === option ? "opacity-100" : "opacity-0")} />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Client-side document capture. There is no upload endpoint in the documented
 * backend, so the file is kept as a data URL in the local draft only.
 */
export function DocumentUpload({
  document,
  onChange,
  label,
  error,
}: {
  document: UploadedDocument | null;
  onChange: (document: UploadedDocument | null) => void;
  label: string;
  error?: string | undefined;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = (file: File) => {
    const invalid = validateUpload(file);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    setLocalError(null);
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => {
      onChange({
        name: file.name,
        size: file.size,
        type: file.type,
        dataUrl: String(reader.result ?? ""),
        uploadedAt: new Date().toISOString(),
      });
      setBusy(false);
    };
    reader.onerror = () => {
      setLocalError("We couldn't read that file. Try another one.");
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  if (document) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
          <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-secondary text-secondary-foreground">
            <FileText className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{document.name}</p>
            <p className="text-xs text-muted-foreground">
              {formatBytes(document.size)} · stored locally
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            <Trash2 className="size-4" /> Remove
          </Button>
        </div>
        <FieldError message={error} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const file = event.dataTransfer.files?.[0];
          if (file) handleFile(file);
        }}
        className={cn(
          "flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-10 text-center transition-colors hover:border-primary/50 hover:bg-muted/60",
          (error || localError) && "border-destructive/60",
        )}
      >
        {busy ? (
          <Loader2 className="size-6 animate-spin text-primary" />
        ) : (
          <UploadCloud className="size-6 text-primary" />
        )}
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">PDF, JPG, PNG or WEBP · up to 5 MB</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/jpeg,image/png,image/webp"
        className="sr-only"
        aria-label={label}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFile(file);
          event.target.value = "";
        }}
      />
      <FieldError message={localError ?? error} />
    </div>
  );
}
